/**
 * Keeps `value` within `[min, max]`. Swapped bounds are put in order; `NaN`
 * stays `NaN` so a broken input is not silently turned into a valid bound.
 */
export function clamp(value: number, min: number, max: number): number {
  const lo = Math.min(min, max);
  const hi = Math.max(min, max);
  return Math.min(Math.max(value, lo), hi);
}

interface Separators {
  decimal: string;
  group: string;
}

const separatorCache = new Map<string, Separators>();

function separatorsOf(locale: string): Separators {
  const cached = separatorCache.get(locale);
  if (cached) return cached;
  let parts: Intl.NumberFormatPart[];
  try {
    parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
  } catch {
    parts = new Intl.NumberFormat('de-DE').formatToParts(12345.6);
  }
  const found: Separators = {
    decimal: parts.find((p) => p.type === 'decimal')?.value ?? ',',
    group: parts.find((p) => p.type === 'group')?.value ?? '.',
  };
  separatorCache.set(locale, found);
  return found;
}

/** Blanks, NBSP, narrow NBSP and apostrophes: grouping marks in fr, de-CH, … */
const SPACE_GROUPS = /[\s  '’]+/g;
const SPACE_MARK = '_';

/** An integer part whose groups (split by `mark`) are 1–3 digits then 3 digits each. */
function isGroupedInteger(text: string, mark: string | null): boolean {
  if (!mark || !text.includes(mark)) return /^\d*$/.test(text);
  const [head, ...rest] = text.split(mark);
  return /^\d{1,3}$/.test(head!) && rest.every((g) => /^\d{3}$/.test(g));
}

/**
 * Parses a number as people type it, returning `null` for anything that is
 * not one number („12 €“, „1e3“, „abc“, „“).
 *
 * The locale (default `de-DE`) decides the ambiguous cases, the rest is read
 * from the input itself:
 * - both `.` and `,` present → the last one is the decimal separator
 *   („1.234,5“ and „1,234.5“ are 1234.5 in every locale);
 * - one separator, repeated → grouping („1.234.567“);
 * - one separator, once → the decimal separator, except that the locale's
 *   own group mark followed by exactly three digits groups („1.234“ is 1234
 *   in de, 1.234 in en; „1,5“ is 1.5 in both);
 * - blanks, NBSP and apostrophes group („1 234,5“, „1’234.5“), and then a
 *   single `.` or `,` is the decimal separator.
 *
 * Groups must be well formed (1–3 digits, then threes) — „1.23.4“ is `null`
 * rather than a guess. Leading `+`, `-` or `−` set the sign.
 */
export function parseLocaleNumber(
  text: string | number | null | undefined,
  locale = 'de-DE'
): number | null {
  if (typeof text === 'number') return Number.isFinite(text) ? text : null;
  if (text === null || text === undefined) return null;
  let s = String(text).trim();
  if (!s) return null;

  let sign = 1;
  const signMatch = /^([+\-−])\s*/.exec(s);
  if (signMatch) {
    if (signMatch[1] !== '+') sign = -1;
    s = s.slice(signMatch[0].length);
  }
  s = s.replace(SPACE_GROUPS, SPACE_MARK);
  if (!/^[\d.,_]+$/.test(s) || !/\d/.test(s)) return null;

  const { group } = separatorsOf(locale);
  const spaced = s.includes(SPACE_MARK);
  const dots = s.split('.').length - 1;
  const commas = s.split(',').length - 1;

  let decimal: string | null = null;
  let mark: string | null = spaced ? SPACE_MARK : null;
  if (dots && commas) {
    if (spaced) return null;
    decimal = s.lastIndexOf('.') > s.lastIndexOf(',') ? '.' : ',';
    mark = decimal === '.' ? ',' : '.';
  } else if (dots || commas) {
    const sep = dots ? '.' : ',';
    const count = dots || commas;
    if (spaced) {
      if (count > 1) return null;
      decimal = sep;
    } else if (count > 1) {
      mark = sep;
    } else if (sep === group && new RegExp(`^[1-9]\\d{0,2}\\${sep}\\d{3}$`).test(s)) {
      mark = sep;
    } else {
      decimal = sep;
    }
  }

  let integer = s;
  let fraction = '';
  if (decimal) {
    const parts = s.split(decimal);
    if (parts.length !== 2) return null;
    [integer, fraction] = parts as [string, string];
    if (!/^\d*$/.test(fraction)) return null;
  }
  if (!isGroupedInteger(integer, mark)) return null;
  const digits = mark ? integer.split(mark).join('') : integer;
  if (!digits && !fraction) return null;

  const value = sign * Number(`${digits || '0'}.${fraction || '0'}`);
  if (!Number.isFinite(value)) return null;
  return value === 0 ? 0 : value;
}
